import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import {
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { getCountryLabel } from '../../helperFunctions/index';

import HeaderSelector from '../headerSelector';

const colorPallete = {
  brand: '#19989B',
  buyer: '#AB8628',
  competitive: '#19989B',
  brandConsumers: '#19989B',
  consumer: '#E45589',
  social: '#19989B',
};

const useStyles = makeStyles(theme => ({
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: '0 50px',
  },
  quarterSelect: {
    outline: 'none',
    border: 'none',
    fontSize: '22px',
    color: props => colorPallete[props.type],
    '&:focus': {
      backgroundColor: 'transparent',
    },

    '@media (max-width: 768px)': {
      fontSize: '18px',
    },

    '@media (min-width: 1300px)': {
      fontSize: '32px',
    },
  },
  quarterSelectDisabled: {
    outline: 'none',
    border: 'none',
    fontSize: '22px',
    color: 'lightgray',

    '@media (max-width: 768px)': {
      fontSize: '18px',
    },

    '@media (min-width: 1300px)': {
      fontSize: '32px',
    },
  },
  dropdownShared: {
    '& svg': {
      top: '5px !important',
    },

    '& ul': {
      maxHeight: '100px',
      overflowY: 'scroll',
    },
  },
  dropdownCountry: {
    width: '90px',

    '@media (max-width: 768px)': {
      width: '70px',
    },

    '@media (min-width: 1300px)': {
      width: '110px',
    },
  },
  dropdownYear: {
    width: '100px',

    '@media (max-width: 768px)': {
      width: '70px',
    },

    '@media (min-width: 1300px)': {
      width: '120px',
    },
  },
  dropdownQuarter: {
    width: '100px',

    '@media (max-width: 768px)': {
      width: '85px',
    },

    '@media (min-width: 1300px)': {
      width: '130px',
    },
  },
  menuItem: {
    minWidth: '200px',
    position: 'relative',
  },
  smallHeading: {
    fontSize: '18px',
    marginBottom: '5px',
    fontWeight: 300,
    color: '#585858',
  },
}));

const buyerBatchSelector = ({
  calculationType,
  onChangeCalculationType,
  pickedBuyerPersona,
  onChangeBuyerPersona,
  pickedBatchData,
  pickedBatchCountry,
  onChangeBatchCountry,
  onChangeBatchQuarter,
  onConfirmBatchQuarterChange,
  onChangeBatchYear,
  showBuyerPicker,
  type,
  customBatchBrandName,
  withConsumerHeading,
  selectedCompetitiveId,
}) => {
  const appData = useSelector(({ data }) => data);
  const classes = useStyles({ type });
  const [shouldTrigerConfirmation, setShouldTrigerConfirmation] = useState(false);
  const isBatchCalculation = calculationType === 'batch';

  const batchQuarterChanged = event => {
    setShouldTrigerConfirmation(true);
    onChangeBatchQuarter(event);
  };

  const batchQuarterClosed = () => {
    if (!shouldTrigerConfirmation) return;

    setShouldTrigerConfirmation(false);
    onConfirmBatchQuarterChange();
  };

  const getCountriesForCustomBatchBrand = (data, brandName) => {
    return Object.entries(data).reduce((result, [country, brands]) => {
      if (brands.hasOwnProperty(brandName)) {
        result.push(country);
      }
      return result;
    }, []);
  };

  const getCompetitiveBatchAndCountrySelection = () => {
    const selectedBrand = appData.batchBrands?.[pickedBatchCountry]?.find(e => e.id === selectedCompetitiveId);
    const availableCountries = Object.keys(selectedBrand.availableBatches);
    let availableBatches = {};
    selectedBrand.availableBatches?.[pickedBatchCountry].forEach(year => {
      availableBatches[year] = appData.existingBatchData?.[pickedBatchCountry]?.[year];
    });

    return { availableCountries, availableBatches };
  };

  let availableBatchCountries;
  let batchDataCodes;

  if (type === 'competitive' && selectedCompetitiveId) {
    const { availableCountries, availableBatches } = getCompetitiveBatchAndCountrySelection();
    availableBatchCountries = availableCountries || [];
    batchDataCodes = availableBatches || {};
  } else {
    availableBatchCountries =
      type === 'competitive'
        ? Object.keys(appData.existingBatchData || {})
        : type === 'brandConsumers'
        ? getCountriesForCustomBatchBrand(appData.yourBrandAndConsumersData, customBatchBrandName)
        : Object.keys(appData.combinedBatchDataCodes || {});
    batchDataCodes =
      type === 'competitive'
        ? { ...appData.existingBatchData?.[pickedBatchCountry] }
        : type === 'brandConsumers'
        ? { ...appData.yourBrandAndConsumersData?.[pickedBatchCountry]?.[customBatchBrandName] }
        : { ...appData.combinedBatchDataCodes?.[pickedBatchCountry] };
  }

  return (
    <FormControl>
      <RadioGroup
        onChange={onChangeCalculationType}
        value={calculationType}
        className={classes.radioGroup}
        aria-labelledby="buyer-batch-row-radio-buttons-group-label"
        name="buyer-batch-radio-buttons-group">
        {pickedBuyerPersona && showBuyerPicker ? (
          <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-end">
            <FormControlLabel
              value="buyer"
              control={<Radio style={{ color: colorPallete[type] }} />}
              label="Audience Persona"
            />
            <HeaderSelector
              selectedId={pickedBuyerPersona.id}
              setPickedId={onChangeBuyerPersona}
              data={appData}
              type="buyer"
              redirect={false}
              bgColor="#AB8628"
              hideHeader
              disabled={isBatchCalculation}
            />
          </Box>
        ) : null}

        {pickedBatchData && (
          <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-end">
            {showBuyerPicker && (
              <FormControlLabel
                value="batch"
                control={<Radio style={{ color: colorPallete[type] }} />}
                label="Consumer Data"
              />
            )}
            {withConsumerHeading && (
              <Typography component="div" variant="body1" className={classes.smallHeading}>
                Consumer Data
              </Typography>
            )}
            <Box display="flex" alignItems="flex-start" justifyContent="flex-end" style={{ gap: '0 20px' }}>
              <FormControl>
                <Select
                  id="batch-country-select"
                  name="batch-country-select"
                  variant="standard"
                  value={pickedBatchCountry}
                  disabled={!isBatchCalculation}
                  onChange={onChangeBatchCountry}
                  disableUnderline
                  classes={{
                    select: isBatchCalculation ? classes.quarterSelect : classes.quarterSelectDisabled,
                  }}
                  className={`${classes.dropdownShared} ${classes.dropdownCountry}`}>
                  {availableBatchCountries.map((country, index) => {
                    return (
                      <MenuItem className={classes.menuItem} key={index} value={country}>
                        {getCountryLabel(country)}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  id="batch-year-select"
                  name="batch-year-select"
                  variant="standard"
                  value={pickedBatchData.year}
                  disabled={!isBatchCalculation}
                  onChange={onChangeBatchYear}
                  disableUnderline
                  classes={{
                    select: isBatchCalculation ? classes.quarterSelect : classes.quarterSelectDisabled,
                  }}
                  className={`${classes.dropdownShared} ${classes.dropdownYear}`}>
                  {Object.keys(batchDataCodes).map((year, index) => {
                    return (
                      <MenuItem className={classes.menuItem} key={index} value={year}>
                        {year}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl>
                <Select
                  id="batch-q-select"
                  name="batch-q-select"
                  variant="standard"
                  multiple
                  value={pickedBatchData.quarter}
                  renderValue={selected => selected.join(', ')}
                  disabled={!isBatchCalculation}
                  onChange={batchQuarterChanged}
                  onClose={batchQuarterClosed}
                  disableUnderline
                  classes={{
                    select: isBatchCalculation ? classes.quarterSelect : classes.quarterSelectDisabled,
                  }}
                  className={`${classes.dropdownShared} ${classes.dropdownQuarter}`}>
                  {batchDataCodes[pickedBatchData.year].map((quarter, index) => {
                    return (
                      <MenuItem className={classes.menuItem} key={index} value={quarter}>
                        <Checkbox
                          style={{ color: colorPallete[type] }}
                          checked={pickedBatchData.quarter.indexOf(quarter) > -1}
                        />
                        <ListItemText primary={quarter} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
          </Box>
        )}
      </RadioGroup>
    </FormControl>
  );
};

export default buyerBatchSelector;
